// 轻量 i18n 核心（无外部依赖）：
// 1) 六语言包静态导入（vite 打包内联），zh 为默认与回退
// 2) 导出 site / pages 两个 Proxy：读取任意字段时触发 i18n.lang 依赖收集，
//    语言切换后模板自动重渲染；脚本内 setup 期解构的旧引用由 App.vue 以 lang 为 key 重挂载兜底
// 3) localStorage 持久化 + <html lang> 同步（无障碍 WCAG 3.1.1）
import { reactive } from 'vue'
import zhSite from '@/content/locales/zh/site.json'
import zhPages from '@/content/locales/zh/pages.json'
import twSite from '@/content/locales/zhtw/site.json'
import twPages from '@/content/locales/zhtw/pages.json'
import enSite from '@/content/locales/en/site.json'
import enPages from '@/content/locales/en/pages.json'
import thSite from '@/content/locales/th/site.json'
import thPages from '@/content/locales/th/pages.json'
import viSite from '@/content/locales/vi/site.json'
import viPages from '@/content/locales/vi/pages.json'
import mySite from '@/content/locales/my/site.json'
import myPages from '@/content/locales/my/pages.json'

export type Lang = 'zh' | 'zhtw' | 'en' | 'th' | 'vi' | 'my'

export interface LangMeta {
  key: Lang
  flag: string
  name: string
  htmlLang: string
}

// 顺序即下拉展示顺序; 默认 zh
export const langs: LangMeta[] = [
  { key: 'zh', flag: '\u{1F1E8}\u{1F1F3}', name: '简体中文', htmlLang: 'zh-CN' },
  { key: 'zhtw', flag: '\u{1F1ED}\u{1F1F0}', name: '繁體中文', htmlLang: 'zh-HK' },
  { key: 'en', flag: '\u{1F1FA}\u{1F1F8}', name: 'English', htmlLang: 'en' },
  { key: 'th', flag: '\u{1F1F9}\u{1F1ED}', name: 'ไทย', htmlLang: 'th' },
  { key: 'vi', flag: '\u{1F1FB}\u{1F1F3}', name: 'Tiếng Việt', htmlLang: 'vi' },
  { key: 'my', flag: '\u{1F1F2}\u{1F1F2}', name: 'မြန်မာ', htmlLang: 'my' }
]

const packs: Record<Lang, { site: unknown; pages: unknown }> = {
  zh: { site: zhSite, pages: zhPages },
  zhtw: { site: twSite, pages: twPages },
  en: { site: enSite, pages: enPages },
  th: { site: thSite, pages: thPages },
  vi: { site: viSite, pages: viPages },
  my: { site: mySite, pages: myPages }
}

const saved = (localStorage.getItem('u2-lang') || 'zh') as Lang
export const i18n = reactive<{ lang: Lang }>({
  lang: packs[saved] ? saved : 'zh'
})

function pick<T extends object>(section: 'site' | 'pages'): T {
  const target = (k: Lang) => (packs[k] as Record<string, T>)[section]
  return new Proxy({} as unknown as T, {
    get(_, p) {
      return (target(i18n.lang) as Record<string | symbol, unknown>)[p]
    },
    has(_, p) {
      return p in target(i18n.lang)
    },
    ownKeys() {
      return Reflect.ownKeys(target(i18n.lang))
    },
    getOwnPropertyDescriptor(_, p) {
      return Reflect.getOwnPropertyDescriptor(target(i18n.lang), p)
    }
  })
}

type SiteShape = typeof zhSite
type PagesShape = typeof zhPages

/** 全站框架文案 + 页脚/导航 (Proxy, 随语言切换自动更新) */
export const site = pick<SiteShape>('site')
/** 各页面正文内容 (Proxy, 随语言切换自动更新) */
export const pages = pick<PagesShape>('pages')

export function currentMeta(): LangMeta {
  return langs.find((l) => l.key === i18n.lang) || langs[0]
}

export function setLang(key: Lang) {
  if (!packs[key]) return
  i18n.lang = key
  localStorage.setItem('u2-lang', key)
  syncHtmlLang()
}

export function syncHtmlLang() {
  document.documentElement.lang = currentMeta().htmlLang
}

syncHtmlLang()
